import React, { useState, useEffect } from 'react';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  Timestamp, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { 
  FaTimesCircle, 
  FaUserSlash, 
  FaUserPlus, 
  FaBan, 
  FaInfoCircle, 
  FaUserFriends 
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const BannedListAdmin = () => {
  const [bannedPeople, setBannedPeople] = useState([]);
  const [form, setForm] = useState({ first: '', last: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  
  const db = getFirestore();

  // Firestore collection name as defined in production
  const BANNED_COLLECTION = "banned_list";

  const fetchBanned = async () => {
    setFetching(true);
    try {
      const q = query(collection(db, BANNED_COLLECTION), orderBy("addedAt", "desc"));
      const snap = await getDocs(q);
      setBannedPeople(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error("Error fetching banned list:", err);
      toast.error("Failed to load banned list");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchBanned();
  }, []);

  const handleAdd = async () => {
    if (!form.first.trim() || !form.last.trim()) {
      return toast.error("First and Last name are required");
    }
    
    setLoading(true);
    try {
      await addDoc(collection(db, BANNED_COLLECTION), {
        firstName: form.first.trim(),
        lastName: form.last.trim(),
        reason: form.reason.trim() || "No reason provided",
        addedAt: Timestamp.now()
      });
      
      setForm({ first: '', last: '', reason: '' });
      fetchBanned();
      toast.error("Individual added to Global Ban List");
    } catch (err) {
      console.error("Error adding to banned list:", err);
      toast.error("System error: Could not save entry");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to remove ${name} from the ban list?`)) return;
    
    try {
      await deleteDoc(doc(db, BANNED_COLLECTION, id));
      fetchBanned();
      toast.info("Individual removed from ban list");
    } catch (err) {
      console.error("Error deleting from banned list:", err);
      toast.error("Could not remove individual");
    }
  };

  return (
    <div className="qr-table-container p-4 shadow-lg border-0" style={{ background: 'var(--brand-dark-glass)' }}>
      {/* Header Section */}
  
      
      {/* Add New Entry Form */}
      <div className="mb-4 p-3 rounded" style={{ border: '1px solid rgba(255,255,255,0.05)' }}>
        <p className="qr-label small mb-3 text-uppercase fw-bold text-white-50">
          <FaUserPlus className="me-2" />Add To Ban List
        </p>
        <Row className="g-2">
          <Col xs={6}>
            <Form.Control 
              placeholder="First Name" 
              value={form.first} 
              onChange={e => setForm({...form, first: e.target.value})} 
              className="glass-input" 
            />
          </Col>
          <Col xs={6}>
            <Form.Control 
              placeholder="Last Name" 
              value={form.last} 
              onChange={e => setForm({...form, last: e.target.value})} 
              className="glass-input" 
            />
          </Col>
          <Col xs={12}>
            <Form.Control 
              placeholder="Reason for ban (e.g., Harassment, Theft, Underage)..." 
              value={form.reason} 
              onChange={e => setForm({...form, reason: e.target.value})} 
              className="glass-input" 
              as="textarea"
              rows={2}
            />
          </Col>
        </Row>
        <button 
          className="qr-btn qr-btn-primary w-100 mt-3 d-flex align-items-center justify-content-center" 
          onClick={handleAdd} 
          disabled={loading}
        >
          {loading ? <Spinner size="sm" className="me-2" /> : <FaBan className="me-2" />}
          COMMIT TO GLOBAL BAN LIST
        </button>
      </div>

      {/* List Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="qr-subtext fw-bold m-0 small" style={{ letterSpacing: '1px' }}>CURRENTLY BANNED INDIVIDUALS</h6>
        <span className="badge rounded-pill bg-danger" style={{ fontSize: '0.7rem' }}>{bannedPeople.length} Records</span>
      </div>

      <div className="qr-card-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
        {fetching ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="light" size="sm" />
            <p className="qr-subtext mt-2 small">Loading registry...</p>
          </div>
        ) : bannedPeople.length > 0 ? (
          bannedPeople.map(p => (
            <div key={p.id} className="qr-log-card d-flex justify-content-between align-items-center mb-2 py-3 shadow-sm   border-1">
              <div className="ps-3">
                <div className="fw-bold text-white" style={{ fontSize: '1.1rem' }}>
                  {p.firstName} {p.lastName}
                </div>
                <div className="qr-subtext-small text-danger fw-bold mt-1">
                  <FaInfoCircle className="me-1" size={12} /> {p.reason}
                </div>
                <div className="text-white-50" style={{ fontSize: '0.65rem' }}>
                  Added: {p.addedAt?.toDate().toLocaleDateString()}
                </div>
              </div>
              <button 
                className="btn btn-link text-white-50 p-0 me-3" 
                onClick={() => handleDelete(p.id, `${p.firstName} ${p.lastName}`)}
                title="Remove Ban"
              >
                <FaTimesCircle size={22} />
              </button>
            </div>
          ))
        ) : (
          <div className="text-center py-5 qr-subtext bg-dark rounded border border-secondary border-dashed" style={{ borderStyle: 'dashed' }}>
            <FaUserFriends size={30} className="opacity-25 mb-2" />
            <p className="m-0">No active bans found in the registry.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BannedListAdmin;